import React, { useId } from "react"
import {
  Avatar, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Paper, Stack, Typography,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined"
import ContactsOutlinedIcon from "@mui/icons-material/ContactsOutlined"
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined"
import Helpers from "../../../../base/utils/Helpers"

const displayValue = (value) => value == null || value === "" ? "Not provided" : value

const Detail = ({ label, value, fullWidth = false }) => (
  <Box sx={{ minWidth: 0, gridColumn: fullWidth ? "1 / -1" : undefined }}>
    <Typography component="dt" variant="caption" sx={{ color: "#61747c", mb: 0.5 }}>{label}</Typography>
    <Typography component="dd" variant="body2" sx={{ m: 0, color: "#233e48", fontWeight: 500, overflowWrap: "anywhere", whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{displayValue(value)}</Typography>
  </Box>
)

const Section = ({ title, icon: Icon, children }) => (
  <Paper component="section" aria-label={title} variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, borderColor: "#dce7e7", minWidth: 0 }}>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
      <Icon sx={{ fontSize: 20, color: "#116c6f" }} />
      <Typography component="h3" variant="subtitle1" sx={{ fontWeight: 600, color: "#234d50" }}>{title}</Typography>
    </Stack>
    {children}
  </Paper>
)

const ApproveOrgMoreInfoModal = ({ open, onClose, orgData, orgLogo }) => {
  const titleId = useId()
  const data = orgData || {}
  const summary = data.grpTmnlSummary || {}
  const name = data.orgName ? Helpers.underscoreRoleFormat(data.orgName) : "Unnamed organization"

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" scroll="paper" aria-labelledby={titleId}
      PaperProps={{ sx: { borderRadius: 3, m: { xs: 1.5, sm: 3 }, width: { xs: "calc(100% - 24px)", sm: "calc(100% - 48px)" }, maxHeight: { xs: "calc(100% - 24px)", sm: "calc(100% - 48px)" } } }}>
      <DialogTitle id={titleId} sx={{ px: { xs: 2, sm: 3 }, py: 2, bgcolor: "#116c6f", color: "white", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
        <Box component="span" sx={{ fontSize: 19, fontWeight: 600 }}>Organization information</Box>
        <IconButton aria-label="Close organization information" onClick={onClose} sx={{ color: "white", flexShrink: 0 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: { xs: 2, sm: 3 }, bgcolor: "#f5f8fa" }}>
        <Stack spacing={2.5}>
          <Paper component="section" aria-label="Organization overview" variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, borderColor: "#dce7e7" }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2.5 }}>
              <Avatar variant="rounded" src={orgLogo ? `data:image/png;base64,${orgLogo}` : undefined} alt={name}
                sx={{ width: { xs: 48, sm: 64 }, height: { xs: 48, sm: 64 }, bgcolor: "#e6f2f1", color: "#116c6f", borderRadius: 2, "& img": { objectFit: "contain" } }}>
                <BusinessOutlinedIcon sx={{ fontSize: 32 }} />
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography component="h2" variant="h5" sx={{ fontSize: { xs: 20, sm: 24 }, color: "#163e40", fontWeight: 700, overflowWrap: "anywhere" }}>{name}</Typography>
                <Typography variant="body2" sx={{ mt: 0.75, color: "#61747c", overflowWrap: "anywhere" }}>
                  Organization code <Box component="span" sx={{ ml: 0.75, fontWeight: 600, color: "#225d60" }}>{displayValue(data.orgCode)}</Box>
                </Typography>
              </Box>
            </Stack>
            <Box component="dl" sx={{ m: 0, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1.5 }}>
              {[["Total groups", summary.totGroups], ["Total terminals", summary.totTerminals], ["Active terminals", summary.activeTerminals]].map(([label, value]) => (
                <Box key={label} sx={{ px: 2, py: 1.5, bgcolor: "#f0f7f6", border: "1px solid #e0edeb", borderRadius: 2, display: { xs: "flex", sm: "block" }, justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                  <Typography component="dt" variant="caption" sx={{ color: "#466b6c" }}>{label}</Typography>
                  <Typography component="dd" sx={{ m: 0, fontSize: 27, fontWeight: 700, color: "#164f51", fontVariantNumeric: "tabular-nums" }}>{value ?? "Not reported"}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>

          <Box>
            <Section title="Contact & organization details" icon={ContactsOutlinedIcon}>
              <Box component="dl" sx={{ m: 0, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 2 }}>
                <Detail label="Primary address" value={data.primAddress} />
                <Detail label="Secondary address" value={data.secAddress} />
                <Detail label="Email" value={data.email} fullWidth />
                <Detail label="Primary contact" value={data.primContact} />
                <Detail label="Secondary contact" value={data.secContact} />
                <Detail label="Description" value={data.description} fullWidth />
              </Box>
            </Section>
          </Box>

          <Section title="Audit history" icon={HistoryOutlinedIcon}>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 2 }}>
              {[
                ["Created", data.createdBy, data.createDateTime ?? data.createdTime],
                ["Updated", data.updatedBy, data.updatedDateTime ?? data.updatedTime],
                ["Approved", data.approvedBy, data.approvedDateTime ?? data.approvedTime],
              ].map(([label, actor, time]) => (
                <Box key={label} sx={{ p: 2, bgcolor: "#f7f9fa", borderRadius: 2, border: "1px solid #eaf0f0", minWidth: 0 }}>
                  <Typography component="h4" variant="body2" sx={{ fontWeight: 600, color: "#225d60", mb: 1.5 }}>{label}</Typography>
                  <Box component="dl" sx={{ m: 0, display: "grid", gap: 1.5 }}>
                    <Detail label={`${label} by`} value={actor} />
                    <Detail label={`${label} time`} value={time} />
                  </Box>
                </Box>
              ))}
            </Box>
          </Section>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.75 }}>
        <Button onClick={onClose} variant="contained" disableElevation sx={{ px: 3, bgcolor: "#116c6f", textTransform: "none", borderRadius: 2, "&:hover": { bgcolor: "#0c5659" } }}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default ApproveOrgMoreInfoModal
